import * as api from "../clients/slashid-api/index"
import { SlashIDOptions } from "./options"

interface Message {
    id: number
}

export interface RequestMessage extends Message {
    type: MessageType
}

export interface ResponseMessage extends Message {
    error: any
}

export enum MessageType {
    StorageGetItem = 0,
    StorageSetItem = 1,

    Ping = 2,

    ChallengeWebauthnCreate = 3,
    ChallengeWebauthnGet = 4,

    SSOChallenge = 5,
}

export class PingRequestMessage implements RequestMessage {
    id: number = randomId()
    type: MessageType = MessageType.Ping
}

export class PingResponseMessage implements ResponseMessage {
    id: number
    error: any

    constructor(id: number, error: any = undefined) {
        this.id = id
        this.error = error
    }
}

export class WebAuthnCreateRequestMessage implements RequestMessage {
    type = MessageType.ChallengeWebauthnCreate
    id: number
    slashIDOptions: SlashIDOptions
    challenge: api.WebAuthnCreateChallenge

    constructor(options: SlashIDOptions, challenge: api.WebAuthnCreateChallenge) {
        this.id = randomId()
        this.slashIDOptions = options
        this.challenge = challenge
    }
}

type SSOChallengeMessage = {
    type: MessageType.SSOChallenge
    urlParams: string
}

export type MessageEventPayload =
    | WebAuthnCreateRequestMessage
    | WebAuthnGetRequestMessage
    | PingRequestMessage
    | SSOChallengeMessage
    | PingRequestMessage

export class WebAuthnCreateResponseMessage implements ResponseMessage {
    id: number
    attestation: api.WebAuthnCreateAttestation | null
    error: any

    constructor(id: number, attestation: api.WebAuthnCreateAttestation | null, error: any = undefined) {
        this.id = id
        this.attestation = attestation
        this.error = error
    }
}
export class WebAuthnGetRequestMessage implements RequestMessage {
    type = MessageType.ChallengeWebauthnGet
    id: number
    slashIDOptions: SlashIDOptions
    challenge: api.WebAuthnGetChallenge

    constructor(options: SlashIDOptions, challenge: api.WebAuthnGetChallenge) {
        this.id = randomId()
        this.slashIDOptions = options
        this.challenge = challenge
    }
}

export class WebAuthnGetResponseMessage implements ResponseMessage {
    id: number
    attestation: api.WebAuthnGetAttestation | null
    error: any

    constructor(id: number, attestation: api.WebAuthnGetAttestation | null, error: any = undefined) {
        this.id = id
        this.attestation = attestation
        this.error = error
    }
}

export class GetItemRequestMessage implements RequestMessage {
    type: MessageType = MessageType.StorageGetItem
    id: number = randomId()

    // Storage.getItem args
    key: string

    constructor(key: string) {
        this.key = key
    }
}

export class GetItemResponseMessage implements ResponseMessage {
    id: number
    error: any

    // Storage.getItem returns
    value: string | null

    constructor(id: number, value: string | null, error: any = undefined) {
        this.id = id
        this.value = value
        this.error = error
    }
}

export class SetItemRequestMessage implements RequestMessage {
    id: number = randomId()
    type: MessageType = MessageType.StorageSetItem

    // Storage.setItem args
    key: string
    value: string

    constructor(key: string, value: string) {
        this.key = key
        this.value = value
    }
}

export class SetItemResponseMessage implements ResponseMessage {
    id: number
    error: any

    constructor(id: number, error: any = undefined) {
        this.id = id
        this.error = error
    }
}

function randomId(): number {
    const rndArray = new Uint32Array(1)
    window.crypto.getRandomValues(rndArray)
    return rndArray[0]
}

export type SSOChallengeParams = {
    ssoChallengeID: string
    ssoCode: string
    csrfToken: string
}

export function getSSOChallengeFromURL(queryString: string): SSOChallengeParams | null {
    const urlParams = new URLSearchParams(queryString)
    const ssoChallengeID = urlParams.get("sso_challenge_id")
    const ssoCode = urlParams.get("sso_code")
    const csrfToken = urlParams.get("csrf_token")

    if (!ssoChallengeID || !ssoCode || !csrfToken) {
        return null
    }

    return { ssoChallengeID, ssoCode, csrfToken }
}

export function isWebAuthnCreateRequestMessage(message: MessageEventPayload): message is WebAuthnCreateRequestMessage {
    return message.type === MessageType.ChallengeWebauthnCreate
}

export function isWebAuthnGetRequestMessage(message: MessageEventPayload): message is WebAuthnGetRequestMessage {
    return message.type === MessageType.ChallengeWebauthnGet
}

export function isSSOChallengeMessage(message: MessageEventPayload): message is SSOChallengeMessage {
    return message && message.type === MessageType.SSOChallenge
}

export function isPingMessage(message: MessageEventPayload): message is PingRequestMessage {
    return message.type === MessageType.Ping
}
